import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Pagination, Collapse, Table, Tag, Input, Select, Space, message, Empty } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import axios from "axios";
import PropTypes from "prop-types";
import { formatDateTime } from "../../helpers/date";
import { formatAmount } from "../../helpers/billingStatement";
import {
  BILLING_TRANSACTION_TYPES,
  BILLING_TRANSACTION_TYPE_COLORS,
  BILLING_TRANSACTION_STATUS_COLORS,
} from "../../constants/billingTransaction";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const BillingTransactionList = ({ initialStatementId }) => {
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ statementId: initialStatementId });
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(initialStatementId ? ["filters"] : []);

  // BillingPage keeps this component mounted across tab switches (Antd Tabs doesn't unmount
  // inactive panes by default), so the initial useState above only seeds the very first open --
  // this effect re-applies the filter (and re-opens the panel so the applied filter is visible)
  // each time a new "Transactions" link is clicked from a different Invoices row.
  useEffect(() => {
    if (!initialStatementId) return;
    setFilters((f) => ({ ...f, statementId: initialStatementId }));
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setFiltersOpen(["filters"]);
  }, [initialStatementId]);

  const buildFilterParams = useCallback(
    () => ({
      ...(filters.merchantId && { merchantId: filters.merchantId }),
      ...(filters.statementId && { statementId: filters.statementId }),
      ...(filters.type && { type: filters.type }),
    }),
    [filters],
  );

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        page: pagination.currentPage,
        limit: pagination.limit,
      };
      const res = await axios.get("/api/billing-transactions", { params });
      setItems(res.data.data);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, buildFilterParams]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const handleFilterChange = (updater) => {
    setFilters(updater);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const clearAllFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "objectId",
      key: "objectId",
      width: 200,
      render: (v) => v || "-",
    },
    {
      title: "Statement No.",
      key: "statementNumber",
      width: 160,
      render: (_, r) => r.statement?.statementNumber || r.statement?.objectId || "-",
    },
    {
      title: "Applies To",
      key: "appliesTo",
      width: 220,
      ellipsis: true,
      // GATEWAY_INTENT-only -- the pending checkout's own statement/CN/DN numbers, resolved
      // from its id arrays server-side (see BillingTransactionController.index). Every other
      // type already shows its one target via Statement No. / Reference.
      render: (_, r) => {
        const numbers = [...(r.statementNumbers || []), ...(r.creditNoteNumbers || []), ...(r.debitNoteNumbers || [])];
        return numbers.length ? numbers.join(", ") : "-";
      },
    },
    {
      title: "Merchant",
      key: "merchant",
      width: 200,
      ellipsis: true,
      // r.merchant is only set on GATEWAY_INTENT rows (no statement to derive it from) --
      // every other type resolves it via statement.merchant instead.
      render: (_, r) =>
        r.statement?.merchant?.name ||
        r.statement?.merchant?.objectId ||
        r.merchant?.name ||
        r.merchant?.objectId ||
        "-",
    },
    {
      title: "User Email",
      dataIndex: "userEmail",
      key: "userEmail",
      width: 220,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 200,
      render: (t) => <Tag color={BILLING_TRANSACTION_TYPE_COLORS[t] || "default"}>{t}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      // Only set on GATEWAY_INTENT rows -- every other type is written already-settled.
      render: (s) => (s ? <Tag color={BILLING_TRANSACTION_STATUS_COLORS[s] || "default"}>{s}</Tag> : "-"),
    },
    {
      title: "Amount",
      key: "amount",
      width: 130,
      align: "right",
      render: (_, r) => <span style={{ fontWeight: 500 }}>{formatAmount(r.amount, r.statement?.currency)}</span>,
    },
    {
      title: "Reference",
      key: "reference",
      width: 200,
      ellipsis: true,
      render: (_, r) =>
        r.creditNote?.documentNumber ||
        r.debitNote?.documentNumber ||
        r.paymentReference ||
        r.remittanceReference ||
        "-",
    },
    {
      title: "Processed At",
      dataIndex: "processedAt",
      key: "processedAt",
      width: 160,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Processed By",
      key: "processedBy",
      width: 160,
      render: (_, r) => r.processedBy?.name || r.processedBy?.email || (r.processedBy ? r.processedBy.objectId : "System"),
    },
  ];

  return (
    <div>
      <Collapse
        activeKey={filtersOpen}
        onChange={setFiltersOpen}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        <Collapse.Panel
          key="filters"
          header={
            <Space>
              <span>Filters</span>
              {activeFilterCount > 0 && <Tag color="blue">{activeFilterCount} active</Tag>}
            </Space>
          }
          extra={
            activeFilterCount > 0 && (
              <a onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}>Clear all</a>
            )
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Merchant Id</span>
                <Input
                  allowClear
                  placeholder="Merchant objectId"
                  value={filters.merchantId || ""}
                  onChange={(e) =>
                    handleFilterChange((f) => ({ ...f, merchantId: e.target.value.trim() || undefined }))
                  }
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Statement Id</span>
                <Input
                  allowClear
                  placeholder="BillingStatement objectId"
                  value={filters.statementId || ""}
                  onChange={(e) =>
                    handleFilterChange((f) => ({ ...f, statementId: e.target.value.trim() || undefined }))
                  }
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Type</span>
                <Select
                  allowClear
                  placeholder="ALL"
                  style={{ width: "100%" }}
                  value={filters.type}
                  onChange={(v) => handleFilterChange((f) => ({ ...f, type: v }))}
                >
                  {BILLING_TRANSACTION_TYPES.map((type) => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </Space>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={activeFilterCount > 0 ? "No transactions match these filters" : "No billing transactions yet"}
              style={{ padding: "24px 0" }}
            >
              {activeFilterCount > 0 && <a onClick={clearAllFilters}>Clear filters</a>}
            </Empty>
          ),
        }}
        style={{ marginTop: 16 }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>
    </div>
  );
};

BillingTransactionList.propTypes = {
  initialStatementId: PropTypes.string,
};

export default BillingTransactionList;
